/**
 * Rebuilding a position from our own fills — ROADMAP 6.2.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * OUR JOURNAL IS THE SOURCE; THE BROKER'S BOOK IS A CHECK
 *
 * The position shown to a trader is folded from the fills we recorded, not
 * read from the broker. That is the inversion this phase exists for, and it
 * is not a preference:
 *
 *   - The broker's position book LAGS. It still reports a position as held
 *     for seconds after the closing order filled, which is exactly the
 *     window that let a repeated Square-off press size itself against a
 *     position already on its way out and REVERSE it.
 *   - It returns EMPTY intermittently (§5.6). A projection cannot vanish;
 *     a read can.
 *   - It cannot answer "how did this get here". A fold over fills can.
 *
 * Divergence between the two raises a `ReconcileItem` (6.3) and never
 * silently overwrites either side.
 *
 * THE CASE THAT IS ALWAYS WRONG SOMEWHERE
 *
 * A fill that crosses through zero — selling 3 lots while long 2 — is one
 * event that both CLOSES a position and OPENS the opposite one. Realising
 * P&L on all 3 against the old average, or carrying the old average into the
 * new short, are the two usual mistakes. The crossing is handled explicitly
 * below and tested in both directions.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { realisedPnl } from './pnl.js';
export const FLAT = {
    netQty: 0,
    avgPrice: 0,
    realisedPnl: 0,
    chargesTotal: 0,
    fillCount: 0,
    lastFillAt: null,
};
const round2 = (v) => Math.round(v * 100) / 100;
/** Prices carry more precision than money: a tick can be 0.05. */
const round4 = (v) => Math.round(v * 10_000) / 10_000;
/**
 * Fold one fill into a running position.
 *
 * Exported because the reconciler applies fills incrementally as they
 * arrive, and replaying the whole history per fill would be O(n²) over a
 * day's trading.
 */
export function applyFill(state, fill) {
    const qty = Math.abs(fill.qty);
    if (!Number.isFinite(qty) || qty === 0 || !Number.isFinite(fill.price))
        return state;
    const signed = fill.side === 'BUY' ? qty : -qty;
    const charges = round2(state.chargesTotal + Math.max(0, fill.charges ?? 0));
    const base = {
        chargesTotal: charges,
        fillCount: state.fillCount + 1,
        lastFillAt: fill.at,
    };
    // ── opening from flat ───────────────────────────────────────────────────
    if (state.netQty === 0) {
        return { ...base, netQty: signed, avgPrice: round4(fill.price), realisedPnl: state.realisedPnl };
    }
    const sameDirection = Math.sign(state.netQty) === Math.sign(signed);
    // ── adding to the position ──────────────────────────────────────────────
    if (sameDirection) {
        const held = Math.abs(state.netQty);
        /*
         * Weighted by SIZE, not a simple mean of the two prices. Averaging
         * 100 and 200 to 150 is only right when the two legs are equal, and it
         * is the error that survives testing because the fixtures usually are.
         */
        const avg = (state.avgPrice * held + fill.price * qty) / (held + qty);
        return {
            ...base,
            netQty: state.netQty + signed,
            avgPrice: round4(avg),
            realisedPnl: state.realisedPnl,
        };
    }
    // ── reducing, closing, or crossing ──────────────────────────────────────
    const held = Math.abs(state.netQty);
    const closing = Math.min(held, qty);
    /*
     * P&L is realised on the CLOSED quantity only, at the existing average,
     * and through `realisedPnl` so the sign convention has one implementation.
     * The signed quantity handed to it is the position's own direction, so a
     * short bought back cheaper comes out positive.
     */
    const realised = round2(state.realisedPnl +
        realisedPnl({
            qty: Math.sign(state.netQty) * closing,
            entryPrice: state.avgPrice,
            exitPrice: fill.price,
        }));
    const netQty = state.netQty + signed;
    if (netQty === 0) {
        // Flat. No average, because there is no open quantity to average.
        return { ...base, netQty: 0, avgPrice: 0, realisedPnl: realised };
    }
    if (Math.sign(netQty) === Math.sign(state.netQty)) {
        // Partially closed. The remaining quantity keeps the average it had —
        // reducing a position does not change what the rest of it cost.
        return { ...base, netQty, avgPrice: state.avgPrice, realisedPnl: realised };
    }
    /*
     * CROSSED THROUGH ZERO. One fill closed the old position and opened the
     * opposite one. The new position's average is THIS fill's price — the old
     * average belongs to a position that no longer exists, and carrying it
     * forward would price the new one off a closed trade.
     */
    return { ...base, netQty, avgPrice: round4(fill.price), realisedPnl: realised };
}
/**
 * Fold a whole history.
 *
 * Sorted by time here rather than trusting the caller: fills arrive from a
 * trade book that is not ordered, and applying them out of order changes the
 * average and can realise P&L against a position that had not opened yet.
 */
export function projectPosition(fills) {
    return [...fills].sort((a, b) => a.at - b.at).reduce(applyFill, FLAT);
}
/**
 * The same fold, split by whoever owned the leg at each fill — ROADMAP 9.51.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS POSSIBLE AT ALL, AND WHY IT NEEDED NO NEW ARITHMETIC
 *
 * A leg can move between trades (9.13), so a single leg's realised P&L can
 * have been earned under two different owners: booked half while loose on
 * the chain, moved into a basket, closed the rest there. `projectPosition`
 * gives all of it to whichever trade holds the leg NOW, which credits the
 * basket with a gain it was not in the position for.
 *
 * The fix needs no second P&L convention because `applyFill` is already an
 * INCREMENTAL projection: it realises P&L on each closing fill, at the
 * average held at that moment. The number is therefore already attributable
 * per fill — it just was not being attributed. This replays the identical
 * sequence and credits each step's delta to the owner at that step.
 *
 * IT TELESCOPES, WHICH IS THE PROPERTY THAT MAKES IT SAFE. Each delta is
 * `next.realisedPnl - state.realisedPnl`, so the deltas sum to the final
 * total by construction. The journal and the drawer cannot disagree about
 * what a leg earned in total, only about how it is divided — which is the
 * whole point.
 *
 * ALTERNATIVES REJECTED. Splitting the leg in two at the moment of the move
 * was the other proposal. It cannot be done honestly: a closing quantity
 * does not align to fill boundaries, so the "closed half" would need numbers
 * written by hand rather than derived from fills, and dividing a position by
 * lot forces a FIFO-or-average choice this codebase has deliberately never
 * made. Nothing is split here. One leg, one history, two columns.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function realisedByOwner(fills, 
/** Which trade held the leg when this fill executed. */
ownerAt) {
    const out = new Map();
    // Sorted for the same reason `projectPosition` sorts: out of order, a fill
    // realises against a position that had not opened yet.
    let state = FLAT;
    for (const fill of [...fills].sort((a, b) => a.at - b.at)) {
        const next = applyFill(state, fill);
        const delta = next.realisedPnl - state.realisedPnl;
        state = next;
        // An entry fill realises nothing. Skipped rather than added as zero, so
        // an owner that only ever added to the position gets no row at all
        // instead of a row saying it earned nothing.
        if (delta === 0)
            continue;
        const owner = ownerAt(fill);
        out.set(owner, (out.get(owner) ?? 0) + delta);
    }
    /*
     * Rounded ONCE at the end. Each delta is already a difference of two
     * two-decimal values, but summing them in floating point drifts — and a
     * journal row reading 17,999.999999999996 is the kind of thing that gets
     * reported as a bug rather than as a rounding artefact.
     */
    for (const [owner, value] of out)
        out.set(owner, round2(value));
    return out;
}
//# sourceMappingURL=positionProjection.js.map